import type {
  AttitudeBehaviorCatalogItem,
  FundamentalCatalogItem,
  PersonalResponse,
} from '@/api/types';

const DEFAULT_COLOR = '#342F82';

/** Identificador del comportamiento o del fundamental, según el nivel emitido. */
export interface FeedbackItem {
  id: string;
  kind: 'fundamental' | 'attitude';
  /** Nombre ya localizado. */
  name: string;
  score: number;
  /** Volumen de puntajes que componen el promedio; no es número de personas. */
  evaluations: number;
  /** Número real de personas que dieron retroalimentación; úsese para todo lo que se
   *  rotule como "colegas" o gobierne una decisión de confianza (barras, kudos,
   *  tarjetas de crecimiento, cabecera del popup). */
  responses: number;
  /** Color del fundamental al que pertenece. */
  color: string;
  /** Clase Font Awesome o URL de imagen; ya resuelto. */
  icon?: string;
  /** Fundamental al que pertenece; igual a `id` cuando `kind` es 'fundamental'. */
  fundamentalId: string;
  /** Orden del fundamental; ordena la lista antes que `order`. */
  fundamentalOrder: number;
  /** Orden dentro del fundamental; 0 cuando el ítem es el fundamental. */
  order: number;
}

function localize(names: Record<string, string> | undefined, lang: string, fallback: string): string {
  if (!names) return fallback;
  return names[lang] ?? names['en'] ?? names['es'] ?? fallback;
}

function toAttitudeItem(
  attitude: AttitudeBehaviorCatalogItem,
  fundamental: FundamentalCatalogItem,
  score: number,
  evaluations: number,
  responses: number,
  lang: string,
): FeedbackItem {
  return {
    id: attitude.id,
    kind: 'attitude',
    name: localize(attitude.names, lang, attitude.id),
    score,
    evaluations,
    responses,
    color: fundamental.color ?? DEFAULT_COLOR,
    // La imagen del comportamiento manda: nombra la conducta concreta, no la familia.
    icon: attitude.image ?? fundamental.icon,
    fundamentalId: fundamental.id,
    fundamentalOrder: fundamental.order,
    order: attitude.order,
  };
}

/**
 * Aplana el catálogo en la lista que la pantalla muestra: comportamientos cuando el
 * fundamental los tiene, el fundamental cuando no. La retroalimentación ocurre al nivel
 * que nombra una conducta concreta; "Actitudes y Comportamientos" no le dice nada a nadie.
 */
export function buildFeedbackItems(
  personalData: PersonalResponse | undefined,
  catalogData: readonly FundamentalCatalogItem[],
  lang: string,
): FeedbackItem[] {
  if (!personalData) return [];

  const items: FeedbackItem[] = [];

  for (const fundamental of catalogData) {
    const scored = personalData.fundamentalScores.find(entry => entry.id === fundamental.id);
    if (!scored) continue;

    const activeAttitudes = fundamental.attitudes.filter(attitude => attitude.isActive);

    if (activeAttitudes.length === 0) {
      items.push({
        id: fundamental.id,
        kind: 'fundamental',
        name: localize(fundamental.shortNames, lang, '')
          || localize(fundamental.names, lang, fundamental.id),
        score: scored.score,
        evaluations: scored.evaluations,
        // Los agregados de fundamental ya cuentan respuestas, no puntajes de daily
        // habit (esa distinción solo existe un nivel más abajo, en el comportamiento).
        responses: scored.evaluations,
        color: fundamental.color ?? DEFAULT_COLOR,
        icon: fundamental.icon,
        fundamentalId: fundamental.id,
        fundamentalOrder: fundamental.order,
        order: 0,
      });
      continue;
    }

    for (const attitude of activeAttitudes) {
      const attitudeScore = scored.attitudes?.find(entry => entry.id === attitude.id);
      items.push(toAttitudeItem(
        attitude,
        fundamental,
        attitudeScore?.score ?? 0,
        attitudeScore?.evaluations ?? 0,
        attitudeScore?.responses ?? 0,
        lang,
      ));
    }
  }

  return items.sort((first, second) =>
    first.fundamentalOrder - second.fundamentalOrder || first.order - second.order);
}
